import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  useMediaQuery,
  useTheme,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Drawer,
  CircularProgress,
  LinearProgress,
  Divider,
  Alert
} from "@mui/material";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DataGrid } from "@mui/x-data-grid";
import { getAllProducts, deleteProduct, downloadProductTemplate, importProducts, toggleFavourite } from "../../service/productService";
import { getAllBrandLists } from "../../service/brandService";
import { getAllCategoryLists } from "../../service/categoryService";
import { useSnackbar } from "../../App";
import AddProduct from "./AddProduct";
import EditProduct from "./EditProduct";
import Color from "../../components/Color";
import { Download, Upload, UploadFile, Visibility, Favorite, FavoriteBorder } from "@mui/icons-material";
import ViewProduct from "./ViewProduct";

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [searchProducts, setSearchProducts] = useState("");
  const [productCodeSearch, setProductCodeSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedProductCode, setDebouncedProductCode] = useState("");
  const [brandFilter, setBrandFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Import progress & result states
  const [importProgressOpen, setImportProgressOpen] = useState(false);
  const [importSuccessOpen, setImportSuccessOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [totalToImport, setTotalToImport] = useState(0);

  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const EditModal = (id) => {
    setSelectedId(id);
    setOpenEditModal(true);
  };

  const ViewModal = (id) => {
    setSelectedId(id);
    setOpenViewModal(true);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/csv",
    ];

    const validExtensions = [".csv", ".xlsx", ".xls"];
    const ext = "." + file.name.split(".").pop().toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      showSnackbar("Please upload only CSV or Excel files (.xlsx, .xls, .csv)", "error");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showSnackbar("File size must be less than 10MB", "error");
      return;
    }

    setSelectedFile(file);
    showSnackbar(`Selected: ${file.name}`, "success");
  };

  const fetchProducts = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const page = (opts.page ?? paginationModel.page) + 1;
        const limit = opts.pageSize ?? paginationModel.pageSize;
        const sortField = sortModel[0]?.field || "id";
        const sortOrder = sortModel[0]?.sort || "desc";

        const res = await getAllProducts(
          page,
          limit,
          debouncedSearch || "",
          debouncedProductCode || "",
          brandFilter !== "All" ? brandFilter : "",
          categoryFilter !== "All" ? categoryFilter : "",
          statusFilter !== "All" ? statusFilter : "",
          sortField,
          sortOrder
        );

        const payload = res?.data;
        let dataArray = [];
        let total = 0;

        if (!payload) {
          dataArray = [];
          total = 0;
        } else if (Array.isArray(payload.data)) {
          dataArray = payload.data;
          total = payload.total ?? dataArray.length;
        } else if (payload.data && Array.isArray(payload.data.data)) {
          dataArray = payload.data.data;
          total = payload.data.total ?? dataArray.length;
        } else if (Array.isArray(payload)) {
          dataArray = payload;
          total = payload.length;
        } else {
          dataArray = payload.data || payload.items || [];
          if (!Array.isArray(dataArray)) dataArray = [];
          total = payload.total ?? (Array.isArray(dataArray) ? dataArray.length : 0);
        }

        const formattedRows = dataArray.map((item) => {
          let displayImage = item?.image_url ?? "";

          // If product has no image or image_url is just the base URL (meaning null), use category image
          if (!displayImage || displayImage === "http://127.0.0.1:8000/" || displayImage.trim() === "") {
            displayImage = item?.category?.image_url ?? "";
          }

          // If still no valid image (category also has no image), use placeholder
          if (!displayImage || displayImage === "http://127.0.0.1:8000/" || displayImage.trim() === "") {
            displayImage = "https://media.istockphoto.com/id/1128826884/vector/no-image-vector-symbol-missing-available-icon-no-gallery-for-this-moment.jpg?s=612x612&w=0&k=20&c=390e76zN_TJ7HZHJpnI7jNl7UBpO3UP7hpR2meE1Qd4=";
          }

          return {
            ...item,
            brandName: item?.brand?.name ?? "--",
            categoryName: item?.category?.name ?? "--",
            display_image: displayImage,
          };
        });

        setRows(formattedRows);
        setRowCount(Number(total) || formattedRows.length);
      } catch (error) {
        showSnackbar("Failed to fetch products!", "error");
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [
      paginationModel.page,
      paginationModel.pageSize,
      debouncedSearch,
      debouncedProductCode,
      brandFilter,
      categoryFilter,
      statusFilter,
      sortModel,
      showSnackbar,
    ]
  );

  useEffect(() => {
    fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
  }, [
    fetchProducts,
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearch,
    debouncedProductCode,
    brandFilter,
    categoryFilter,
    statusFilter,
    sortModel,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(searchProducts);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchProducts]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedProductCode(productCodeSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [productCodeSearch]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [brandsRes, categoriesRes] = await Promise.all([
          getAllBrandLists(),
          getAllCategoryLists(),
        ]);
        setBrands(brandsRes?.data?.data ?? brandsRes?.data ?? []);
        setCategories(categoriesRes?.data?.data ?? categoriesRes?.data ?? []);
      } catch (err) {
        showSnackbar("Failed to load brand/category filters", "error");
      }
    };
    fetchFilters();
  }, [showSnackbar]);

  const clearFilters = () => {
    setSearchProducts("");
    setProductCodeSearch("");
    setDebouncedSearch("");
    setDebouncedProductCode("");
    setBrandFilter("All");
    setCategoryFilter("All");
    setStatusFilter("All");
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setDebouncedSearch(searchProducts);
    setDebouncedProductCode(productCodeSearch);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setDrawerOpen(false);
  };

  const handleReset = () => {
    clearFilters();
    setDrawerOpen(true);
  };

  const handleDeleteClick = (id) => {
    if (!id) {
      showSnackbar("No product selected!", "error");
      return;
    }
    setDeleteId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteProduct(deleteId);
      showSnackbar("Product deleted successfully!", "success");
      fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to delete product!", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleToggleFavourite = async (id) => {
    try {
      const res = await toggleFavourite(id);
      if (res?.data?.status === "success") {
        showSnackbar(res.data.message || "Favourite status updated!", "success");
        fetchProducts();
      }
    } catch (error) {
      showSnackbar("Failed to update favourite status!", "error");
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      minWidth: isMobile ? 80 : 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          size="small"
          sx={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 1.5,
            padding: isMobile ? "10px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.08)",
            },
            alignItems: 'center'
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    {
      field: "is_favourite",
      headerName: "Fav",
      flex: 0.4,
      minWidth: isMobile ? 60 : 80,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleToggleFavourite(params.row.id);
          }}
          size="small"
        >
          {params.value ? (
            <Favorite sx={{ color: "error.main", fontSize: isMobile ? 20 : 24 }} />
          ) : (
            <FavoriteBorder sx={{ fontSize: isMobile ? 20 : 24 }} />
          )}
        </IconButton>
      ),
    },
    {
      field: "display_image",
      headerName: "Image",
      flex: .7,
      minWidth: isMobile ? 100 : 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <img
            src={params.value}
            alt={params.row.name || "No Image"}
            style={{
              width: isMobile ? 35 : 40,
              height: isMobile ? 35 : 40,
              objectFit: "cover",
              borderRadius: "6px",
              border: '2px solid lightgray'
            }}
            onError={(e) => {
              e.target.src = "https://media.istockphoto.com/id/1128826884/vector/no-image-vector-symbol-missing-available-icon-no-gallery-for-this-moment.jpg?s=612x612&w=0&k=20&c=390e76zN_TJ7HZHJpnI7jNl7UBpO3UP7hpR2meE1Qd4=";
            }}
          />
        </Box>
      )
    },
    { field: "short_code", headerName: "Short Code", flex: 1, minWidth: isMobile ? 120 : 130, headerAlign: "center", align: "center" },
    {
      field: "hsn_code",
      headerName: "HSN Code",
      flex: 0.9,
      minWidth: isMobile ? 110 : 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => params.value || "---"
    },
    {
      field: "name",
      headerName: "Product Name",
      flex: 1.2,
      minWidth: isMobile ? 150 : 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
          <Typography
            variant="body2"
            onClick={() => ViewModal(params.row.id)}
            sx={{
              color: Color.primary.main,
              cursor: "pointer",
              fontWeight: 500,
              lineHeight: "normal",
              "&:hover": {
                textDecoration: "none",
              },
            }}
          >
            {params.value}
          </Typography>
        </Box>
      )
    },
    { field: "categoryName", headerName: "Category", flex: 1, minWidth: 150, headerAlign: "center", align: "center" },
    {
      field: "unit",
      headerName: "Unit",
      flex: 0.8,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const qty = parseFloat(params.row.unit_quantity || params.row.units_quantity || 0);
        const unit = params.row.units || params.row.unit || "";
        if (!qty && !unit) return "--";
        const formattedQty = qty % 1 === 0 ? parseInt(qty) : qty;
        return `${formattedQty} ${unit}`;
      }
    },
    {
      field: "base_price",
      headerName: "Base Price",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const gstRate = parseFloat(params.row.gst_percentage ?? params.row.tax_rate ?? 0);
        const isInclusive = params.row.price_includes_tax !== false;
        const inclusive = isInclusive ? (params.row.inclusive_price ?? params.row.base_price) : params.row.base_price;
        const base = params.row.base_price != null
          ? params.row.base_price
          : (parseFloat(inclusive || 0) / (1 + gstRate / 100));
        return base != null && base !== "" ? `₹${parseFloat(base).toFixed(2)}` : "--";
      }
    },
    {
      field: "inclusive_price",
      headerName: "Price (Incl. Tax)",
      flex: 1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const val = params.row.price_includes_tax !== false
          ? (params.row.inclusive_price ?? params.row.base_price)
          : params.row.base_price;
        return val != null && val !== "" ? `₹${parseFloat(val).toFixed(2)}` : "--";
      }
    },
    {
      field: "gst_percentage",
      headerName: "GST %",
      flex: 0.8,
      minWidth: isMobile ? 90 : 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const val = params.value ?? params.row.tax_rate;
        return val != null && val !== "" ? `${parseFloat(val)}%` : "0%";
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      minWidth: isMobile ? 100 : 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "error"}
          size="small"
          sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
        />
      ),
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1 }} sx={{ padding: isMobile ? 1 : 0 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent={isMobile ? "stretch" : { sm: "flex-start", md: "flex-start", lg: "space-between" }}
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <TextField
          value={searchProducts}
          onChange={(e) => setSearchProducts(e.target.value)}
          placeholder="Search product, code, HSN..."
          variant="outlined"
          InputLabelProps={{ shrink: false }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchProducts && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchProducts("");
                    setDebouncedSearch("");
                  }}
                >
                  <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputLabel-root": { display: "none" },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
              height: isMobile ? "34px" : "37.69px",
              fontSize: isMobile ? "0.85rem" : "1rem",
            },
            width: isMobile ? "100%" : { sm: 180, md: 220, lg: 250 },
            mb: isMobile ? 0 : 0,
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2 }}>
          <IconButton
            sx={{
              color: Color.secondary.main,
              fontSize: isMobile ? "24px" : "30px",
              border: "1px solid",
              borderColor: Color.secondary.main,
              borderRadius: "4px",
              height: isMobile ? "34px" : "44px",
              width: isMobile ? "34px" : "44px",
            }}
            onClick={() => setDrawerOpen(true)}
            size="small"
          >
            <FilterAltIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
          </IconButton>
          <Button
            sx={{
              background: Color.info.main,
              color: "white",
              height: isMobile ? "40px" : "45px",
              width: isMobile ? "100%" : { sm: 190, md: 200, lg: 220 },
              whiteSpace: "nowrap",
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": {
                background: Color.info.main,
              },
            }}
            onClick={() => setImportOpen(true)}
            startIcon={<UploadFile />}
            size="medium"
          >
            Import Product
          </Button>
          <Button
            sx={{
              background: Color.primary.main,
              color: "white",
              height: isMobile ? "40px" : "45px",
              width: isMobile ? "100%" : { sm: 170, md: 180, lg: 200 },
              whiteSpace: "nowrap",
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
            onClick={() => setOpenAddModal(true)}
            startIcon={<AddIcon />}
            size="medium"
          >
            Add Product
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 2 : 2 }}>
        <DataGrid
          rows={rows}
          columns={columns.map((col) => ({
            ...col,
            flex: col.flex ?? 1,
            minWidth: col.minWidth ?? (isMobile ? 80 : 100),
          }))}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
          }}
          rowCount={rowCount}
          rowHeight={isMobile ? 48 : 56}
          paginationMode="server"
          sortModel={sortModel}
          onSortModelChange={(newModel) => {
            const defaultSort = [{ field: "id", sort: "desc" }];
            const finalModel = newModel.length ? newModel : defaultSort;
            if (JSON.stringify(finalModel) !== JSON.stringify(sortModel)) {
              setSortModel(finalModel);
            }
          }}
          sortingMode="server"
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu
              disableColumnSorting
          autoHeight
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 600,
              px: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              px: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            ViewModal(selectedRow?.id);
            handleMenuClose();
          }}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <Visibility fontSize="small" sx={{ mr: 1, color: "info.main" }} /> View
        </MenuItem>
        <MenuItem
          onClick={() => {
            EditModal(selectedRow?.id);
            handleMenuClose();
          }}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDeleteClick(selectedRow?.id);
          }}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setDrawerOpen(false);
          }
        }}
        PaperProps={{
          sx: {
            width: isMobile ? "60vw" : 420,
            boxShadow: 4,
            borderRadius: "8px 0 0 8px",
          },
        }}
        sx={{
          zIndex: 1300,
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(129, 128, 128, 0.05)",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1}
            mt={isMobile ? 1 : 2}
            borderBottom="1px solid #e0e0e0"
          >
            <Typography fontSize={isMobile ? "1rem" : 18} fontWeight={600}>
              Filter Products
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
            </IconButton>
          </Box>

          <Box p={isMobile ? 1 : 2} sx={{ flexGrow: 1, overflowY: "auto" }}>
            <form onSubmit={handleSearch}>
              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12}>
                  <TextField
                    value={searchProducts}
                    onChange={(e) => setSearchProducts(e.target.value)}
                    placeholder=" Product name"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: false }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchProducts && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchProducts("");
                              setDebouncedSearch("");
                            }}
                          >
                            <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                      },
                      width: isMobile ? "100%" : 185,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    value={productCodeSearch}
                    onChange={(e) => setProductCodeSearch(e.target.value)}
                    placeholder="Short code"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: false }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: productCodeSearch && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setProductCodeSearch("");
                              setDebouncedProductCode("");
                            }}
                          >
                            <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                      },
                      width: isMobile ? "100%" : 185,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Select
                      value={brandFilter}
                      onChange={(e) => setBrandFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "153%" : 185,
                      }}
                    >
                      <MenuItem value="All">Select Brand</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "130%" : 185,
                      }}
                    >
                      <MenuItem value="All">Select Category</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "150%" : 185,
                      }}
                    >
                      <MenuItem value="All">Select Status</MenuItem>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="left"
                gap={isMobile ? 1 : 2}
                mt={isMobile ? 2 : 4}
              >
                <Button
                  type="submit"
                  sx={{
                    background: Color.primary.main,
                    color: "white",
                    height: isMobile ? "40px" : "45px",
                    width: isMobile ? "100%" : 120,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                    "&:hover": {
                      background: Color.primary.dark,
                    },
                  }}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>

                <Button
                  onClick={handleReset}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "40px" : "45px",
                    width: isMobile ? "100%" : 120,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Reset
                </Button>

                <Button
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "40px" : "45px",
                    width: isMobile ? "100%" : 120,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Close
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "auto",
            m: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete the product{" "}
            <strong>{selectedRow?.name ?? "this product"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={handleCancelDelete}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              height: isMobile ? "40px" : "45px",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              height: isMobile ? "40px" : "45px",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Product Dialog */}
      <Dialog
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setSelectedFile(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            m: isMobile ? 1 : 3,
            maxWidth: 580,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "1.5rem", fontWeight: 600, pb: 1 }}>
          Bulk Import Products
          <IconButton
            onClick={() => {
              setImportOpen(false);
              setSelectedFile(null);
            }}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, pb: 2 }}>
          <Box
            sx={{
              bgcolor: "#f0f7ff",
              borderRadius: 2,
              p: 2.5,
              mb: 3,
              borderLeft: "5px solid",
              borderColor: Color.primary.main,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
              How to Import Items
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ol" sx={{ pl: 2.5, m: 0 }}>
              <li>Download the Excel/CSV template using the button below</li>
              <li>Fill in your items data following the template format (enter final GST-inclusive price in the price column)</li>
              <li>Supported GST rates: 0%, 5%, 18%, 40% (blank defaults to 0%)</li>
              <li>Upload the completed file to import all items at once</li>
            </Typography>
          </Box>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<Download />}
            sx={{
              height: 52,
              borderColor: Color.primary.main,
              color: Color.primary.main,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              mb: 3,
              "&:hover": {
                borderColor: Color.primary.dark,
                bgcolor: "rgba(124, 11, 204, 0.04)",
              },
            }}
            onClick={async () => {
              try {
                const res = await downloadProductTemplate();
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'Product_Import_Template.csv';
                a.click();
                window.URL.revokeObjectURL(url);
                showSnackbar("CSV Template downloaded!", "success");
              } catch (err) {
                showSnackbar("Failed to download template", "error");
              }
            }}
          >
            DOWNLOAD EXCEL/CSV TEMPLATE
          </Button>

          {selectedFile && (
            <Box
              sx={{
                bgcolor: "success.light",
                color: "white",
                px: 3,
                py: 1.5,
                borderRadius: 2,
                mb: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body1" fontWeight={600}>
                Selected: {selectedFile.name}
              </Typography>
              <Typography variant="caption">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              border: "2px dashed",
              borderColor: selectedFile ? "success.main" : "#90caf9",
              borderRadius: 2,
              bgcolor: selectedFile ? "#f0fff4" : "#f8fbff",
              height: 200,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s",
              "&:hover": {
                bgcolor: selectedFile ? "#e8f5e8" : "#e3f2fd",
                borderColor: selectedFile ? "success.dark" : "#42a5f5",
              },
            }}
            onClick={() => document.getElementById("import-file-input")?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) handleFileSelect(file);
            }}
          >
            <UploadFile sx={{ fontSize: 60, color: selectedFile ? "success.main" : "#42a5f5", mb: 1 }} />
            <Typography variant="h6" fontWeight={600} color={selectedFile ? "success.main" : "inherit"}>
              {selectedFile ? "Ready to Import!" : "Click or drag file here"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Supports: .xlsx, .xls, .csv • Max 10MB
            </Typography>
          </Box>

          <input
            id="import-file-input"
            type="file"
            accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 4, gap: 2 }}>
          <Button
            onClick={() => {
              setImportOpen(false);
              setSelectedFile(null);
            }}
            sx={{
              minWidth: 100,
              height: isMobile ? "40px" : "45px",
              border: "1px solid #ddd",
              color: "text.primary",
              fontWeight: 500,
              textTransform: "none",
              borderRadius: 2,
              ml: 10
            }}
          >
            CANCEL
          </Button>

          <Button
            variant="contained"
            startIcon={importing ? <CircularProgress size={20} /> : <Upload />}
            disabled={!selectedFile || importing}
            sx={{ 
              minWidth: 160, 
              bgcolor: selectedFile ? Color.primary.main : "#e0e0e0", 
              color: "white",
              height: isMobile ? "40px" : "45px" 
            }}
            onClick={async () => {
              if (!selectedFile) return;

              setImporting(true);
              setImportProgressOpen(true);
              setProgress(0);
              setCurrentCount(0);
              setTotalToImport(0);
              setImportResult(null);

              try {
                const response = await importProducts(selectedFile);
                const result = response?.data?.data || response?.data;

                const total = result.total_rows || 0;
                const success = result.success_count || 0;
                const failed = result.failed_count || 0;

                setTotalToImport(total);
                setImportResult({
                  total_rows: total,
                  success_count: success,
                  created_count: result.created_count ?? success,
                  updated_count: result.updated_count || 0,
                  failed_count: failed
                });

                let count = 0;
                const interval = setInterval(() => {
                  count += Math.floor(Math.random() * 18) + 8;
                  if (count >= total) { count = total; clearInterval(interval); }
                  setCurrentCount(count);
                  setProgress((count / total) * 100);
                }, 70);

                setTimeout(() => {
                  clearInterval(interval);
                  setCurrentCount(total);
                  setProgress(100);
                  setTimeout(() => {
                    setImportProgressOpen(false);
                    setImportSuccessOpen(true);
                    fetchProducts();
                  }, 600);
                }, total > 100 ? 2500 : 1800);

                showSnackbar(`"${selectedFile.name}" imported successfully!`, "success");
              } catch (error) {
                setImportProgressOpen(false);

                const errResponse = error?.response?.data || {};
                const errData = errResponse.data || {};
                const failedRows = errData.failed_rows || [];
                const totalFailed = errData.failed_count || 0;
                const totalRows = errData.total_rows || 0;

                // Handle specifically the aborted import due to validation errors (e.g. Category not found)
                if (errResponse.status === "error" && errResponse.message?.includes("Import aborted due to validation errors")) {
                  setImportResult({
                    isValidationError: true,
                    total_rows: totalRows,
                    success_count: 0,
                    failed_count: totalFailed,
                    failed_rows: failedRows,
                    serverMessage: errResponse.message
                  });
                  setImportSuccessOpen(true);
                  showSnackbar(errResponse.message, "error");
                  return;
                }

                const isOnlyDuplicate = failedRows.length > 0 && failedRows.every(row =>
                  row.errors.every(err => err.toLowerCase().includes("duplicate short code"))
                );

                if (isOnlyDuplicate && totalFailed > 0) {
                  const duplicateGroups = {};
                  failedRows.forEach(item => {
                    item.errors.forEach(err => {
                      const matches = err.match(/'([^']+)'/g) || [];
                      matches.forEach(codeStr => {
                        const code = codeStr.replace(/'/g, '');
                        if (!duplicateGroups[code]) duplicateGroups[code] = [];
                        duplicateGroups[code].push(item.row);
                      });
                    });
                  });

                  setImportResult({
                    isDuplicateError: true,
                    duplicateGroups,
                    total_rows: totalRows,
                    failed_count: totalFailed
                  });
                  setImportSuccessOpen(true);
                  showSnackbar("Duplicate short codes found!", "warning");
                } else if (errData.status === "error" && failedRows.length > 0) {
                  setImportResult({
                    isValidationError: true,
                    total_rows: totalRows,
                    success_count: 0,
                    failed_count: totalFailed,
                    failed_rows: failedRows
                  });
                  setImportSuccessOpen(true);
                  showSnackbar("Validation errors in uploaded file", "error");
                } else {
                  const msg = error?.response?.data?.message || "Import failed!";
                  showSnackbar(msg, "error");
                }
              } finally {
                setImporting(false);
                setSelectedFile(null);
                setImportOpen(false);
              }
            }}
          >
            {importing ? "Importing..." : "Import Items"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DUPLICATE ERROR DIALOG - FULLY SAFE */}
      <Dialog open={importSuccessOpen && importResult?.isDuplicateError} onClose={() => setImportSuccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h5" fontWeight={700} color="error.main" gutterBottom>
            Duplicate Short Codes Found!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Import stopped to prevent data conflict
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 4 }}>
          {importResult ? (
            <Box sx={{ my: 3 }}>
              <Grid container spacing={2} sx={{ textAlign: 'center', mb: 4, justifyContent: 'center' }}>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold">{importResult.total_rows || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Rows</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold" color="success.main">0</Typography>
                  <Typography variant="body2" color="text.secondary">Success</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold" color="error.main">{importResult.failed_count || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Failed</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>Duplicate Short Codes:</Typography>
              <Box sx={{ maxHeight: 300, overflowY: "auto", mt: 2 }}>
                {Object.keys(importResult.duplicateGroups || {}).length === 0 ? (
                  <Typography textAlign="center" color="text.secondary">No duplicates found</Typography>
                ) : (
                  Object.entries(importResult.duplicateGroups || {}).map(([code, rows]) => (
                    <Box key={code} sx={{ bgcolor: "error.light", color: "white", borderRadius: 2, p: 2, mb: 2, textAlign: "center" }}>
                      <Typography variant="h6" fontWeight="bold">{code}</Typography>
                      <Typography variant="body2">Rows: <strong>{rows.sort((a, b) => a - b).join(", ")}</strong></Typography>
                    </Box>
                  ))
                )}
              </Box>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <strong>{importResult.failed_count || 0} rows</strong> were blocked due to duplicate short codes.
                Please fix them and try again.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => setImportSuccessOpen(false)}
            sx={{
              minWidth: 200,
              bgcolor: "error.main",
              "&:hover": { bgcolor: "error.dark" }
            }}
          >
            Fix File & Retry
          </Button>
        </DialogActions>
      </Dialog>

      {/* VALIDATION ERROR DIALOG - FULLY SAFE (e.g., Category 'Cakes' not found) */}
      <Dialog open={importSuccessOpen && importResult?.isValidationError} onClose={() => setImportSuccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h5" fontWeight={700} color="error.main" gutterBottom>
            Import Failed - Validation Errors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {importResult?.serverMessage || "No products were imported. Please fix the errors below."}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 4 }}>
          {importResult ? (
            <>
              <Grid container spacing={2} sx={{ textAlign: 'center', mb: 4, justifyContent: 'center' }}>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold">{importResult.total_rows || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Rows</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold" color="success.main">0</Typography>
                  <Typography variant="body2" color="text.secondary">Imported</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h5" fontWeight="bold" color="error.main">{importResult.failed_count || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Failed</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>Error Details:</Typography>
              <Box sx={{ maxHeight: 300, overflowY: "auto", mt: 2 }}>
                {(importResult.failed_rows || []).length === 0 ? (
                  <Typography textAlign="center" color="text.secondary">No error details available</Typography>
                ) : (
                  importResult.failed_rows.map((item) => (
                    <Box key={item.row} sx={{ bgcolor: "error.light", color: "white", borderRadius: 2, p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Row {item.row}</Typography>
                      <Typography variant="body2">
                        {item.errors.join(" | ")}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>

              <Alert severity="error" sx={{ mt: 3 }}>
                All rows must be valid for the import to proceed.
              </Alert>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => setImportSuccessOpen(false)}
            sx={{
              minWidth: 200,
              bgcolor: "error.main",
              "&:hover": { bgcolor: "error.dark" }
            }}
          >
            Fix File & Retry
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUCCESS DIALOG - FULLY SAFE */}
      <Dialog open={importSuccessOpen && !importResult?.isDuplicateError && !importResult?.isValidationError} onClose={() => setImportSuccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "success.main" }} />
          <Typography variant="h6" fontWeight={600}>Import Completed Successfully!</Typography>
          <Typography variant="body2" color="text.secondary">
            {importResult?.total_rows || 0} rows processed
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 4 }}>
          {importResult ? (
            <Grid container spacing={2} sx={{ textAlign: "center", mt: 0.5 }}>
              {[
                { label: "New products added", value: importResult.created_count, color: "success.main", bg: "#e8f5e9" },
                { label: "Duplicates updated", value: importResult.updated_count, color: "warning.main", bg: "#fff8e1" },
                { label: "Failed", value: importResult.failed_count, color: "error.main", bg: "#ffebee" },
              ].map((stat) => (
                <Grid item xs={4} key={stat.label}>
                  <Box sx={{ bgcolor: stat.bg, borderRadius: 2, py: 2, px: 1 }}>
                    <Typography variant="h4" fontWeight="bold" color={stat.color}>{stat.value || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Duplicates are existing products with the same short code; their details were replaced by the imported values.
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => setImportSuccessOpen(false)}
            sx={{
              minWidth: 200,
              bgcolor: Color.primary.main,
              "&:hover": { bgcolor: Color.primary.dark }
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* LIVE PROGRESS DIALOG */}
      <Dialog open={importProgressOpen} hideBackdrop PaperProps={{ sx: { borderRadius: 3, width: 440, maxWidth: "95vw", boxShadow: 24 } }}>
        <DialogContent sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={80} thickness={5} sx={{ color: Color.primary.main, mb: 3 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>Importing Products...</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Please wait while we process your file</Typography>

          <Box sx={{ width: "100%", px: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="h5" fontWeight="bold">{currentCount} / {totalToImport || "..."}</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">{progress.toFixed(0)}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 12, borderRadius: 2, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: Color.primary.main } }} />
          </Box>

          {progress === 100 && (
            <Typography color="success.main" fontWeight={600} variant="h6" sx={{ mt: 3 }}>Completed!</Typography>
          )}
        </DialogContent>
      </Dialog>

      <AddProduct
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSuccess={() => {
          setOpenAddModal(false);
          fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
        }}
      />

      <EditProduct
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        productId={selectedId}
        onUpdate={() => {
          setOpenEditModal(false);
          fetchProducts({ page: paginationModel.page, pageSize: paginationModel.pageSize });
        }}
      />

      <ViewProduct
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        productId={selectedId}
      />
    </Box>
  );
};

export default Products;